import { dropTask } from 'ember-concurrency';
import { fn } from '@ember/helper';
import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import { not } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { setSemesterCodeString } from '../../../utils/format';
import * as v from 'valibot';
import { t } from 'ember-intl';
import { tracked, cached } from '@glimmer/tracking';
import { trackedObject } from '@ember/reactive/collections';
import {
  getSingleValueForTasField,
  setSingleValueForTasField,
  getConfigForTasField,
  buildValidationSchemaForProgramTemplateFields,
  validateAndCollectErrors,
} from '../../../utils/tuition-assistance/fields.ts';
import Component from '@glimmer/component';
import errorsForField from '../../../helpers/errors-for-field.ts';
import Input from 'tio-reimagined/components/input';
import Textarea from 'tio-reimagined/components/textarea';
import ErrorMessages from 'tio-reimagined/components/error-messages';
import TuitionAssistanceProgramFormField from '../program-form-field.gts';
import type { FieldName, ValidationField } from '../../../utils/tuition-assistance/fields.ts';
import type { CourseDateValidationResult } from '../forms/courses/course-duration.gts';
import type FeaturesService from '../../../services/features';
import type IntlService from 'ember-intl/services/intl';
import type TasApplicationModel from '../../../models/tas-application.ts';
import type { TemplateFieldName } from '../../../utils/tuition-assistance/fields.ts';
import type Owner from '@ember/owner';

export interface TuitionAssistanceProgramDetailsEditCourseDurationDialogSignature {
  Args: {
    application: TasApplicationModel;
    isOpen: boolean;
    onClose: () => void;
  };
}

const changelogSchema = v.pipe(v.string('Comment is required'), v.nonEmpty('Comment is required'));

export default class TuitionAssistanceProgramDetailsEditCourseDurationDialogComponent extends Component<TuitionAssistanceProgramDetailsEditCourseDurationDialogSignature> {
  @service declare intl: IntlService;
  @service declare features: FeaturesService;

  applicationFieldsCopy!: TasApplicationModel['fields'];
  @tracked hasSubmitted = false;
  @tracked submitError = null;
  @tracked changelog = '';

  constructor(
    owner: Owner,
    args: TuitionAssistanceProgramDetailsEditCourseDurationDialogSignature['Args']
  ) {
    super(owner, args);
    this.applicationFieldsCopy = trackedObject(this.args.application.fields);
  }

  submit = () => {
    try {
      this.saveFields.perform();
    } catch (e) {
      console.error(e);
    }
  };

  get instance() {
    return this.args.application?.tasProgramInstance;
  }

  get template() {
    return this.instance?.tasProgramTemplate;
  }

  get programHasCoursesPreApprovalSubmissionMaxLeadTime() {
    return !!this.template.coursesPreApprovalSubmissionMaxLeadTime;
  }

  formatDateForValidation(date: string) {
    return date ? new Date(date) : undefined;
  }

  get firstDayToSubmitApplicationForPreApproval(): CourseDateValidationResult | null {
    if (!this.programHasCoursesPreApprovalSubmissionMaxLeadTime) {
      return null;
    }

    const numberOfDays = Number(this.template.coursesPreApprovalSubmissionMaxLeadTime) || null;
    if (!numberOfDays) {
      return null;
    }

    const today = new Date();
    const firstDayToSubmit = new Date(today.getTime() + numberOfDays * 24 * 60 * 60 * 1000);
    const formattedDate = this.intl.formatDate(firstDayToSubmit, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    return {
      date: firstDayToSubmit,
      message: `Your employer requires that you submit your application within ${numberOfDays} days of the start date. Change date to be on or before ${formattedDate}.`,
    };
  }

  get courseMaximumBeginDateWithErrorMessaging(): CourseDateValidationResult {
    if (!this.firstDayToSubmitApplicationForPreApproval) {
      return {
        message: this.intl.t(
          'tuition_assistance.program_details.courses.validation.defaults.begin_date'
        ),
        date: new Date('2099-01-01'),
      };
    }

    return this.firstDayToSubmitApplicationForPreApproval;
  }

  get lastPossibleBeginDateWithErrorMessaging(): CourseDateValidationResult | null {
    let numberOfDays: number | null = null;
    let policyMessage = '';

    const { lastDayToSubmitApplicationForPreApproval, coursesPreApprovalSubmissionMinLeadTime } =
      this.template;

    if (lastDayToSubmitApplicationForPreApproval) {
      numberOfDays = -Number(lastDayToSubmitApplicationForPreApproval) || null;
      policyMessage = numberOfDays
        ? `Your employer requires that you submit your application within ${Math.abs(
            numberOfDays
          )} days after the start date.`
        : '';
    } else if (coursesPreApprovalSubmissionMinLeadTime) {
      numberOfDays = Number(coursesPreApprovalSubmissionMinLeadTime) || null;
      policyMessage = numberOfDays
        ? `Your employer requires that you submit your application at a minimum of ${numberOfDays} days before the start date.`
        : '';
    }

    if (!numberOfDays) {
      return null;
    }

    const today = new Date();
    const lastDayToSubmit = new Date(today.getTime() + numberOfDays * 24 * 60 * 60 * 1000);

    return {
      date: lastDayToSubmit,
      message: policyMessage,
    };
  }

  get courseMinimumBeginDateWithErrorMessaging(): CourseDateValidationResult {
    const lastDayToSubmit = this.lastPossibleBeginDateWithErrorMessaging;

    if (!lastDayToSubmit) {
      return {
        message: this.intl.t(
          'tuition_assistance.program_details.courses.validation.defaults.begin_date'
        ),
        date: new Date('2000-01-01'),
      };
    }

    return lastDayToSubmit;
  }

  get courseDateValidation() {
    const fields = this.applicationFieldsCopy;
    const courseStartDate = getSingleValueForTasField('COURSES_BEGIN_DATE', fields);
    const courseEndDate = getSingleValueForTasField('COURSES_END_DATE', fields);
    const courseReceiptDate = getSingleValueForTasField('COURSES_RECEIPT_DATE', fields);
    const toReturn = {
      COURSES_BEGIN_DATE:
        typeof courseStartDate === 'string'
          ? this.formatDateForValidation(courseStartDate)
          : courseStartDate,
      COURSES_END_DATE:
        typeof courseEndDate === 'string'
          ? this.formatDateForValidation(courseEndDate)
          : courseEndDate,
      COURSES_RECEIPT_DATE:
        typeof courseReceiptDate === 'string'
          ? this.formatDateForValidation(courseReceiptDate)
          : courseReceiptDate,
      SEMESTER_CODE: getSingleValueForTasField('SEMESTER_CODE', fields),
    };

    return toReturn;
  }

  get isFormValid() {
    const fieldsToCheck: TemplateFieldName[] = ['COURSES_BEGIN_DATE', 'COURSES_END_DATE'];

    const requiredFields: TemplateFieldName[] = fieldsToCheck.filter((name) => {
      const config = getConfigForTasField(name, this.template.fields);
      return config?.visible === true;
    });

    const allDatesPresent = requiredFields.every((field) =>
      Boolean(getSingleValueForTasField(field, this.applicationFieldsCopy))
    );

    const noChangelogErrors = this.changelogValidationErrors.length === 0;

    return allDatesPresent && noChangelogErrors;
  }

  get formValidationErrors() {
    return validateAndCollectErrors(this.courseDateValidation, this.dynamicValidationSchema);
  }

  get changelogValidationErrors() {
    const result = v.safeParse(changelogSchema, this.changelog);
    if (!result.success) {
      return result.issues.map((issue) => issue.message);
    }
    return [];
  }

  @cached
  get dynamicValidationSchema() {
    const courseEndDateMinimum = this.template.isCertificateProgram
      ? '2000-01-01'
      : 'COURSES_BEGIN_DATE';

    const fields: ValidationField[] = [
      {
        name: 'COURSES_BEGIN_DATE',
        rules: {
          type: 'date',
          max: this.courseMaximumBeginDateWithErrorMessaging.date,
          min: this.courseMinimumBeginDateWithErrorMessaging.date,
        },
        errors: {
          max: this.courseMaximumBeginDateWithErrorMessaging.message,
          min: this.courseMinimumBeginDateWithErrorMessaging.message,
        },
      },
      {
        name: 'COURSES_END_DATE',
        rules: { type: 'date', min: courseEndDateMinimum, max: '2099-01-01' },
        errors: {
          min: 'Date must after Start Date',
          max: 'Date must be before Jan 1, 2099',
        },
      },
      {
        name: 'COURSES_RECEIPT_DATE',
        rules: { type: 'date' },
      },
      {
        name: 'SEMESTER_CODE',
        rules: { type: 'string' },
      },
    ];

    return buildValidationSchemaForProgramTemplateFields(fields, this.template);
  }

  get semesterCode() {
    return getSingleValueForTasField('SEMESTER_CODE', this.applicationFieldsCopy) || '';
  }

  setSemesterCode = (date: string) => {
    const semesterCode = setSemesterCodeString(date);
    setSingleValueForTasField('SEMESTER_CODE', semesterCode, this.applicationFieldsCopy);
  };

  updateValueForField = (fieldName: FieldName, value: string) => {
    // @ts-expect-error: not sure
    setSingleValueForTasField(fieldName, value, this.applicationFieldsCopy);
    if (fieldName === 'COURSES_BEGIN_DATE' && this.template.semesterCodeIsVisible) {
      this.setSemesterCode(value);
    }
  };

  saveFields = dropTask(async () => {
    this.hasSubmitted = true;
    this.submitError = null;
    if (!this.isFormValid) {
      return;
    }

    try {
      this.args.application.fields = this.applicationFieldsCopy;
      await this.args.application.save({ adapterOptions: { changelog: this.changelog } });
      this.args.onClose();
    } catch (e) {
      console.error(e);
      this.submitError = e;
    }
  });

  <template>
    <Modal @isOpen={{@isOpen}} @onClose={{@onClose}} @size="xl">
      <:header>
        <h3 class="font-semibold text-midnight">
          {{t "tuition_assistance.program_details.courses.course_information"}}
        </h3>
        <h4 class="font-semibold">
          {{t "tuition_assistance.program_details.courses.please_enter_begin_and_end_option_two"}}
        </h4>
      </:header>
      <:body>
        <form
          class="flex flex-col flex-wrap gap-2 md:gap-4 justify-center items-center"
          aria-label="Edit Course Duration Dialog"
        >
          <div class="w-full md:w-64">
            <TuitionAssistanceProgramFormField
              @name="COURSES_BEGIN_DATE"
              @programTemplate={{@application.tasProgramInstance.tasProgramTemplate}}
              as |field|
            >
              <Input
                id={{field.inputId}}
                @type="date"
                @value={{getSingleValueForTasField field.name this.applicationFieldsCopy}}
                @onChange={{fn this.updateValueForField field.name}}
                @errors={{errorsForField
                  "COURSES_BEGIN_DATE"
                  schemaErrors=this.formValidationErrors
                }}
              />
            </TuitionAssistanceProgramFormField>
          </div>

          {{#if @application.tasProgramInstance.tasProgramTemplate.courseReceiptDateVisible}}
            <div class="w-full md:w-64">
              <TuitionAssistanceProgramFormField
                @name="COURSES_RECEIPT_DATE"
                @programTemplate={{@application.tasProgramInstance.tasProgramTemplate}}
                as |field|
              >
                <Input
                  id={{field.inputId}}
                  @type="date"
                  @value={{getSingleValueForTasField field.name this.applicationFieldsCopy}}
                  @onChange={{fn this.updateValueForField field.name}}
                  @errors={{errorsForField
                    "COURSES_RECEIPT_DATE"
                    schemaErrors=this.formValidationErrors
                  }}
                />
              </TuitionAssistanceProgramFormField>
            </div>
          {{/if}}

          <div class="w-full md:w-64">
            <TuitionAssistanceProgramFormField
              @name="COURSES_END_DATE"
              @programTemplate={{@application.tasProgramInstance.tasProgramTemplate}}
              as |field|
            >
              <Input
                id={{field.inputId}}
                @type="date"
                @value={{getSingleValueForTasField field.name this.applicationFieldsCopy}}
                @onChange={{fn this.updateValueForField field.name}}
                @errors={{errorsForField "COURSES_END_DATE" schemaErrors=this.formValidationErrors}}
              />
            </TuitionAssistanceProgramFormField>
          </div>
          {{#if @application.tasProgramInstance.tasProgramTemplate.semesterCodeIsVisible}}
            <div class="w-full md:w-64">
              <TuitionAssistanceProgramFormField
                @name="SEMESTER_CODE"
                @programTemplate={{@application.tasProgramInstance.tasProgramTemplate}}
                as |field|
              >
                <Input
                  id={{field.inputId}}
                  @value={{this.semesterCode}}
                  @errors={{errorsForField field.name schemaErrors=this.formValidationErrors}}
                  disabled={{true}}
                />
              </TuitionAssistanceProgramFormField>
            </div>
          {{/if}}
          <div class="w-full md:w-64">
            <label class="flex font-semibold text-sm text-tio-gray-700 block mb-1">
              {{t "comment"}}
              <span class="text-error-400 ml-1">*</span>
            </label>
            <Textarea
              @value={{this.changelog}}
              @onChange={{fn (mut this.changelog)}}
              @errors={{this.changelogValidationErrors}}
              class="changelog-editable__changelog-field w-full"
            />
          </div>
        </form>
        <ErrorMessages @showErrors={{this.hasSubmitted}} @error={{this.submitError}} class="mb-2" />
      </:body>
      <:footer>
        <Footer
          @onSubmit={{this.submit}}
          @isRunning={{this.saveFields.isRunning}}
          @onCancel={{@onClose}}
          @submitDisabled={{not this.isFormValid}}
        />
      </:footer>
    </Modal>
  </template>
}
